import { View, Text, ScrollView, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from '@dramadetector/shared';
import { ShareCopyLinkButton } from './ShareCopyLinkButton';

export interface ExplicitSharedResultData {
  link_type: 'explicit_result';
  score: number | null;
  summary: string | null;
}

/**
 * Deliberately narrow renderer for explicit public result links. It remains
 * independent of the full analysis screen so it cannot request source data.
 */
export function ExplicitSharedResult({
  shortId,
  result,
}: {
  shortId: string;
  result: ExplicitSharedResultData;
}) {
  const { t } = useTranslation('share');

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={['#0a0012', '#1a0a2e', '#0d1117']}
        style={StyleSheet.absoluteFill}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      />
      <ScrollView contentContainerStyle={styles.content}>
        <Text style={styles.eyebrow}>
          {t('share.explicit_result.title', { defaultValue: 'Shared result' })}
        </Text>
        {result.score !== null && (
          <View style={styles.card}>
            <Text style={styles.label}>
              {t('share.explicit_result.score_label', { defaultValue: 'Drama Score' })}
            </Text>
            <Text style={styles.score}>{result.score.toFixed(1)}/10</Text>
          </View>
        )}
        {result.summary !== null && (
          <View style={styles.card}>
            <Text style={styles.label}>
              {t('share.explicit_result.summary_label', { defaultValue: 'Summary' })}
            </Text>
            <Text style={styles.summary}>{result.summary}</Text>
          </View>
        )}
        <ShareCopyLinkButton shortId={shortId} testID="share-copy-link-button" />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { padding: 24, gap: 16 },
  eyebrow: { color: '#c4b5fd', fontSize: 14, fontWeight: '700', textTransform: 'uppercase' },
  card: { backgroundColor: '#ffffff12', borderColor: '#ffffff24', borderWidth: 1, borderRadius: 16, padding: 20 },
  label: { color: '#c4b5fd', fontSize: 14, fontWeight: '600' },
  score: { color: '#fff', fontSize: 42, fontWeight: '800', marginTop: 8 },
  summary: { color: '#fff', fontSize: 18, lineHeight: 27, marginTop: 8 },
});
